import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ApontamentoForm from '@/components/paineis/apontamentos/ApontamentoForm';
import ApontamentosLista from '@/components/paineis/apontamentos/ApontamentosLista';
import BarraFiltros from '@/components/paineis/filtros/BarraFiltros';
import OrigemDadosCard from '@/components/paineis/OrigemDadosCard';
import PainelCard from '@/components/paineis/PainelCard';
import { useToast } from '@/contexts/ToastContext';
import { useApontamentos } from '@/hooks/useApontamentos';
import { useCatalogoApontamento } from '@/hooks/useCatalogoApontamento';
import { useCatalogoProdutos } from '@/hooks/useCatalogoProdutos';
import { useFiltroPaineis } from '@/hooks/useFiltroPaineis';
import { dashboardsService } from '@/services/dashboards.service';
import type {
  ApontamentoReuniao,
  ApontamentosResponse,
  FiltroApontamentos,
  NovoApontamento,
  RecortePainel,
} from '@/types/dashboards';
import {
  errosDaApi,
  formDoApontamento,
  formVazio,
  hojeIso,
  mesDaData,
  type CatalogoApontamento,
} from '@/utils/equiplexApontamentos';
import { descreverPeriodo, PERIODO_PERSONALIZADO } from '@/utils/filtroPaineis';

const Cabecalho: FC = () => (
  <header>
    <p className="text-xs uppercase tracking-wide text-content-muted">Painéis · Equiplex</p>
    <h1 className="text-2xl font-bold text-content-primary">Apontamentos da reunião</h1>
    <p className="mt-1 text-sm text-content-secondary">
      O que a reunião registra sobre a produção: paradas, faltas, problemas. Explica os desvios, não muda nenhum número
      dos painéis.
    </p>
  </header>
);

const SELECT_FILTRO = 'rounded-lg border border-theme-border bg-surface-primary px-2 py-1 text-xs text-content-primary';

const FiltroMaquina: FC<{ catalogo: CatalogoApontamento; maquina: string; onMaquina: (m: string) => void }> = ({
  catalogo,
  maquina,
  onMaquina,
}) => (
  <div className="flex items-center gap-2 text-xs text-content-muted">
    <span aria-hidden>Máquina</span>
    <select className={SELECT_FILTRO} value={maquina} onChange={(e) => onMaquina(e.target.value)} aria-label="Filtrar por máquina">
      <option value="">Todas</option>
      {catalogo.grupos.map((g) => (
        <optgroup key={g.rotulo} label={g.rotulo}>
          {g.maquinas.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  </div>
);

interface PropsLista {
  estado: ReturnType<typeof useApontamentos>;
  recorte?: RecortePainel | null;
  onEditar: (a: ApontamentoReuniao) => void;
  onApagar: (a: ApontamentoReuniao) => Promise<void>;
  onCsv: () => void;
}

const BotaoCsv: FC<{ onCsv: () => void; vazio: boolean }> = ({ onCsv, vazio }) => (
  <button
    type="button"
    onClick={onCsv}
    disabled={vazio}
    data-testid="apontamentos-csv"
    className="inline-flex items-center gap-1 rounded-lg border border-theme-border px-3 py-1.5 text-xs text-content-secondary hover:bg-surface-hover disabled:opacity-50"
  >
    <span aria-hidden className="material-symbols-outlined text-[16px] leading-none">download</span>
    Baixar CSV
  </button>
);

const CorpoLista: FC<{ resposta: ApontamentosResponse | null; erro: string | null } & Pick<PropsLista, 'onEditar' | 'onApagar'>> = ({
  resposta,
  erro,
  onEditar,
  onApagar,
}) => {
  if (erro) return <p className="text-sm text-theme-danger">{erro}</p>;
  if (!resposta) return <p className="text-sm text-content-muted">Carregando…</p>;
  if (resposta.apontamentos.length === 0) {
    return (
      <p className="text-sm text-content-muted" data-testid="apontamentos-vazio">
        Nenhum apontamento neste filtro.
      </p>
    );
  }
  return <ApontamentosLista apontamentos={resposta.apontamentos} onEditar={onEditar} onApagar={onApagar} testId="apontamentos-lista" />;
};

const Lista: FC<PropsLista> = ({ estado, recorte, onEditar, onApagar, onCsv }) => {
  const total = estado.resposta?.apontamentos.length ?? 0;
  return (
    <PainelCard
      testId="apontamentos-registrados"
      titulo="Registrados"
      subtitulo={recorte ? `${descreverPeriodo(recorte)} · ${total} apontamento(s)` : `${total} apontamento(s)`}
      acoes={<BotaoCsv onCsv={onCsv} vazio={total === 0} />}
    >
      <CorpoLista resposta={estado.resposta} erro={estado.erro} onEditar={onEditar} onApagar={onApagar} />
    </PainelCard>
  );
};

interface PropsCartao {
  opcoes: ApontamentosResponse['opcoes'];
  catalogo: CatalogoApontamento | null;
  editando: ApontamentoReuniao | null;
  novo: { n: number; inicial: NovoApontamento };
  onCriar: (dados: NovoApontamento) => Promise<void>;
  onSalvarEdicao: (dados: NovoApontamento) => Promise<void>;
  onCancelarEdicao: () => void;
}

/** Novo apontamento ou, com um escolhido na lista, a edição dele (a tela sobe até o formulário). */
const CartaoFormulario: FC<PropsCartao> = ({ opcoes, catalogo, editando, novo, onCriar, onSalvarEdicao, onCancelarEdicao }) => {
  const ancora = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (editando) ancora.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [editando]);

  if (!editando) {
    return (
      <PainelCard
        testId="apontamento-novo"
        titulo="Novo apontamento"
        subtitulo="Quem registra aparece na lista; só quem registrou (ou um admin) edita ou apaga."
      >
        <ApontamentoForm key={novo.n} opcoes={opcoes} catalogo={catalogo} inicial={novo.inicial} rotuloSalvar="Registrar" onSalvar={onCriar} />
      </PainelCard>
    );
  }
  return (
    <div ref={ancora} className="scroll-mt-6">
      <PainelCard
        testId="apontamento-editar"
        titulo="Editar apontamento"
        subtitulo={`Registrado por ${editando.autor.nome}. A mudança fica no histórico.`}
      >
        <ApontamentoForm
          key={editando.id}
          opcoes={opcoes}
          catalogo={catalogo}
          inicial={formDoApontamento(editando)}
          rotuloSalvar="Salvar alteração"
          onSalvar={onSalvarEdicao}
          onCancelar={onCancelarEdicao}
        />
      </PainelCard>
    </div>
  );
};

/**
 * Link antigo (`?mes=2026-10`, de antes do filtro dos painéis): vira o período daquele mês do
 * retrato, uma vez, e sai da URL. Devolve `true` enquanto a troca está pendente — a lista espera,
 * para não buscar duas vezes.
 */
function useMesLegado(catalogo: CatalogoApontamento | null): boolean {
  const [busca, setBusca] = useSearchParams();
  const chave = busca.get('mes');
  useEffect(() => {
    if (!chave || !catalogo) return;
    const mes = catalogo.meses.find((m) => m.key === chave);
    setBusca(
      (atual) => {
        const proxima = new URLSearchParams(atual);
        proxima.delete('mes');
        if (mes) {
          proxima.delete('periodo');
          proxima.set('inicio', mes.start);
          proxima.set('fim', mes.end);
        }
        return proxima;
      },
      { replace: true },
    );
  }, [chave, catalogo, setBusca]);
  return !!chave && !!catalogo;
}

/** Sem período no filtro, a lista abre no mês de produção corrente (o que a reunião olha). */
function consultaDaLista(params: Record<string, string>, maquina: string): FiltroApontamentos {
  const comPeriodo = params.periodo || params.inicio ? params : { ...params, periodo: 'mes_atual' };
  return maquina ? { ...comPeriodo, maquina } : comPeriodo;
}

/**
 * Apontamentos da reunião (`/paineis/equiplex/apontamentos`): quem acessa os painéis
 * registra o que aconteceu (data, máquina, motivo, quantidade…); quem registrou — ou um
 * admin — edita e apaga, com histórico. Máquinas, SKUs e meses vêm do retrato da Visão geral.
 * A lista usa o filtro dos painéis (período e produto, na URL) — com produto, o apontamento
 * sem SKU continua, porque vale para todos.
 */
const EquiplexApontamentosPage: FC = () => {
  const { showToast } = useToast();
  const { catalogo, carregando: carregandoCatalogo } = useCatalogoApontamento();
  const legadoPendente = useMesLegado(catalogo);
  const { filtro: filtroPaineis, params, definir, limpar } = useFiltroPaineis();
  const produtos = useCatalogoProdutos();
  const [maquina, setMaquina] = useState('');
  const filtro = useMemo(() => consultaDaLista(params, maquina), [params, maquina]);
  const estado = useApontamentos(filtro, !carregandoCatalogo && !legadoPendente);
  const recorte = estado.resposta?.recorte;
  const [editando, setEditando] = useState<ApontamentoReuniao | null>(null);
  // Remonta o formulário de novo apontamento depois de salvar, mantendo data e máquina.
  const [novo, setNovo] = useState(() => ({ n: 0, inicial: formVazio(hojeIso()) }));

  /** Salvou com data fora do período: a lista vai para o mês dela, senão o apontamento "some". */
  const recarregarNoMesDa = async (data: string) => {
    const foraDoPeriodo = recorte && (data < recorte.inicio || data > recorte.fim);
    const doMes = foraDoPeriodo && catalogo ? mesDaData(data, catalogo.meses) : undefined;
    if (doMes) definir({ ...filtroPaineis, periodo: PERIODO_PERSONALIZADO, inicio: doMes.start, fim: doMes.end });
    else await estado.carregar();
  };

  const criar = async (dados: NovoApontamento) => {
    await dashboardsService.criarApontamento(dados);
    showToast('Apontamento registrado.', 'success');
    setNovo((v) => ({ n: v.n + 1, inicial: { ...formVazio(dados.data), maquina: dados.maquina } }));
    await recarregarNoMesDa(dados.data);
  };

  const salvarEdicao = async (dados: NovoApontamento) => {
    if (!editando) return;
    await dashboardsService.editarApontamento(editando.id, dados);
    showToast('Apontamento atualizado.', 'success');
    setEditando(null);
    await recarregarNoMesDa(dados.data);
  };

  const apagar = async (a: ApontamentoReuniao) => {
    try {
      await dashboardsService.apagarApontamento(a.id);
      showToast('Apontamento apagado (fica no histórico).', 'success');
      await estado.carregar();
    } catch (e) {
      showToast(errosDaApi(e, 'Não foi possível apagar o apontamento.').geral, 'error');
    }
  };

  const baixarCsv = async () => {
    try {
      await dashboardsService.baixarApontamentosCsv(filtro);
    } catch (e) {
      showToast(errosDaApi(e, 'Não foi possível gerar o CSV.').geral, 'error');
    }
  };

  const opcoes = estado.resposta?.opcoes;
  return (
    <div className="flex flex-col gap-6 p-6 lg:p-10">
      <Cabecalho />
      <OrigemDadosCard chave="apontamentos" />
      {opcoes && (
        <CartaoFormulario
          opcoes={opcoes}
          catalogo={catalogo}
          editando={editando}
          novo={novo}
          onCriar={criar}
          onSalvarEdicao={salvarEdicao}
          onCancelarEdicao={() => setEditando(null)}
        />
      )}
      <BarraFiltros
        filtro={filtroPaineis}
        onChange={definir}
        onLimpar={limpar}
        recorte={recorte ?? undefined}
        catalogo={produtos}
        recalculando={estado.carregando}
        erro={null}
        indisponivel={false}
      />
      {catalogo && <FiltroMaquina catalogo={catalogo} maquina={maquina} onMaquina={setMaquina} />}
      <Lista estado={estado} recorte={recorte} onEditar={setEditando} onApagar={apagar} onCsv={baixarCsv} />
    </div>
  );
};

export default EquiplexApontamentosPage;
